<template>
  <div id="__UsageReportsDetail">
    <!-- Basic information -->
    <CSection separate>
      <CTitle large margin-bottom>
        {{ $t('Basic information') }}
      </CTitle>

      <CRow gutter="md">
        <CCol col="12" v-for="(val, key) in detailBasic" :key="key">
          <LabelValue :label="$t(key)" :value="$t(val)" v-if="val === 'yes' || val === 'no'" />
          <LabelValue :label="$t(key)" :value="val" v-else />
        </CCol>
      </CRow>
    </CSection>

    <!-- Budget -->
    <CSection separate v-if="groupBy === 'account' || groupBy === 'acc_group'">
      <CTitle large margin-bottom>
        {{ $t('Budget setting') }}
      </CTitle>

      <template v-if="!detailBudget.length">
        {{ $t('not set yet') }}
      </template>

      <template v-else>
        <div class="__UsageReportsDetail-BudgetTotal">
          <LabelValue :label="$t('Budget total')">
            <template v-slot:value>
              <CCurrency :value="detailBudgetTotal" type="usd" />
            </template>
          </LabelValue>
        </div>

        <CRow gutter="md">
          <CCol col="12" v-for="val in detailBudget" :key="val.date">
            <LabelValue :label="formatDate(val.date)">
              <template v-slot:value>
                <CCurrency :value="val.value" type="usd" />
              </template>
            </LabelValue>
          </CCol>
        </CRow>
      </template>
    </CSection>

    <!-- Alert -->
    <CSection v-if="groupBy === 'account'">
      <CTitle large margin-bottom>
        {{ $t('Alert setting') }}
      </CTitle>

      <CRow gutter="md">
        <CCol col="12" v-for="(val, key) in detailAlert" :key="key">
          <LabelValue :label="$t(key)" :value="$t(val)" />
        </CCol>
      </CRow>
    </CSection>

    <!-- Account list - For account group -->
    <CSection v-if="groupBy === 'acc_group'">
      <CTitle large margin-bottom>
        {{ $t('Accounts') }}
      </CTitle>

      <List>
        <Item v-for="(val, index) of detailGroupAccounts" :key="index">
          <ItemSection>
            <ItemLabel>{{ val.name }}</ItemLabel>
            <ItemLabel caption>ID: {{ val.id }}</ItemLabel>
          </ItemSection>
        </Item>
      </List>
    </CSection>

    <!-- Tag key and Tag value list - For cross tag -->
    <CSection v-if="groupBy === 'cross_tag'">
      <CTitle large margin-bottom>
        {{ $t('Tag key / Tag value') }}
      </CTitle>

      <List>
        <Item v-for="(val, index) of detailTagKeyValues" :key="index">
          <ItemSection>
            <ItemLabel><span class="text-bold">Key:</span> {{ val.key }}</ItemLabel>

            <ItemLabel caption><span class="text-bold">Value:</span> {{ val.value }}</ItemLabel>
          </ItemSection>
        </Item>
      </List>
    </CSection>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import { formatDate } from '@/lib/dates';

import List from '@design-system/components/List.vue';
import Item from '@design-system/components/Item.vue';
import ItemSection from '@design-system/components/ItemSection.vue';
import ItemLabel from '@design-system/components/ItemLabel.vue';
import LabelValue from '@design-system/components/LabelValue.vue';

import CSection from '@/components/uikit/CSection';
import CRow from '@/components/uikit/CRow';
import CCol from '@/components/uikit/CCol';
import CTitle from '@/components/uikit/CTitle';
import CCurrency from '@/components/uikit/CCurrency';

const messages = {
  en: {
    'Basic information': 'Basic Info',
    'Budget setting': 'Budget Settings',
    'Budget total': 'Budget Total',
    'Alert setting': 'Alert Settings',
    Accounts: 'Accounts',
    name: 'Name',
    id: 'ID',
    payer: 'Payer',
    payer_id: 'Payer ID',
    yes: 'Yes',
    no: 'No',
    state: 'State',
    set: 'Set',
    'not set yet': 'Not set yet',
  },
  ja: {
    'Basic information': '基本情報',
    'Budget setting': '予算の設定',
    'Budget total': '予算合計',
    'Alert setting': 'アラートの設定',
    Accounts: 'アカウント',
    name: '名前',
    id: 'ID',
    payer: '支払いアカウント',
    payer_id: '支払いアカウントID',
    yes: 'はい',
    no: 'いいえ',
    state: '状態',
    set: '設定済み',
    'not set yet': '未設定',
  },
};

export default Vue.extend({
  name: 'Detail',
  i18n: { messages },
  components: {
    List,
    Item,
    ItemSection,
    ItemLabel,
    LabelValue,
    CSection,
    CRow,
    CCol,
    CTitle,
    CCurrency,
  },
  computed: {
    ...mapState('usageReports', {
      groupBy: state => state.groupBy,
      loading: state => state.loading.fetchDetail,
    }),

    ...mapGetters('usageReports', ['getFormattedDetail']),
    ...mapGetters('budget', ['getBudget', 'getBudgetTotal']),

    detailBasic() {
      return this.getFormattedDetail.basic ? this.getFormattedDetail.basic : null;
    },

    detailBudgetTotal() {
      return this.getBudgetTotal;
    },

    detailBudget() {
      return this.getBudget;
    },

    detailAlert() {
      return this.getFormattedDetail.alert ? this.getFormattedDetail.alert : null;
    },

    detailGroupAccounts() {
      return this.getFormattedDetail.accounts ? this.getFormattedDetail.accounts : [];
    },

    detailTagKeyValues() {
      return this.getFormattedDetail.tags ? this.getFormattedDetail.tags : [];
    },
  },
  methods: {
    formatDate(date) {
      return formatDate(date, 'YtoM');
    },
  },
});
</script>

<style lang="scss" scoped>
.__UsageReportsDetail {
  &-BudgetTotal {
    padding-bottom: $space-xs;
    margin-bottom: $space-md;
    border-bottom: 1px dashed $border-color;
  }
}
</style>
